import * as THREE from 'three';
import type { BattleState } from '@somnhold/sim';
import { GlowLayer } from '../engine/glow.ts';
import { cameraRight, eyeHeight, looksRng, SkyLook, type OmenCtx, type OmenScene } from './common.ts';

/**
 * The fog (the view's own scene fog closes in on the camera, see battle-screen): banks of mist roll
 * low over the ground, every hero carries a lantern's pool of light, and the nightmares' eyes glint
 * through the grey before their shapes do.
 */

const BANK_VERT = /* glsl */ `
attribute vec4 aBank; // x, z, size, seed
uniform float uTime;
varying vec2 vUv;
varying float vSeed;
varying vec2 vW;
void main() {
  vUv = uv * 2.0 - 1.0;
  vSeed = aBank.w;
  // Each bank drifts slowly with the wind and wraps around the field.
  vec2 c = aBank.xy + vec2(uTime * 0.18, uTime * 0.05) * (0.6 + aBank.w);
  c = mod(c + 18.0, 36.0) - 18.0;
  vec3 p = vec3(c.x + position.x * aBank.z, 0.2 + aBank.w * 0.6, c.y + position.y * aBank.z);
  vW = p.xz;
  gl_Position = projectionMatrix * viewMatrix * vec4(p, 1.0);
}`;

const BANK_FRAG = /* glsl */ `
uniform float uTime;
uniform vec3 uColor;
uniform float uAlpha;
varying vec2 vUv;
varying float vSeed;
varying vec2 vW;
float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float noise(vec2 p) {
  vec2 i = floor(p), f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash(i), hash(i + vec2(1, 0)), u.x), mix(hash(i + vec2(0, 1)), hash(i + vec2(1, 1)), u.x), u.y);
}
void main() {
  float r = length(vUv);
  float n = noise(vW * 0.7 + vec2(uTime * 0.1, vSeed * 10.0)) * 0.6 + noise(vW * 1.9 - uTime * 0.15) * 0.4;
  float a = (1.0 - smoothstep(0.2, 1.0, r)) * smoothstep(0.25, 0.75, n) * uAlpha;
  if (a < 0.01) discard;
  gl_FragColor = vec4(uColor, a);
  #include <colorspace_fragment>
}`;

const EYE = new THREE.Color('#ffb070');
const EYE_HOT = new THREE.Color('#ff4f3a');
const LAMP = new THREE.Color('#ffd9a8');

export const buildFog = (ctx: OmenCtx): OmenScene => {
  const group = new THREE.Group();
  group.name = 'omen-fog';
  const low = ctx.low;
  const rnd = looksRng(0xf06);
  const sky = new SkyLook(ctx, { hemi: ['#a8b0be', '#2c3036', 0.8], key: ['#d4dbe6', 0.55], rim: ['#b4c0d4', 0.8] });
  const n = low ? 14 : 34;
  const geo = new THREE.InstancedBufferGeometry();
  // Unrotated: the shader lays the quad's x and y flat on the ground as x and z.
  const quad = new THREE.PlaneGeometry(2, 2);
  geo.index = quad.index;
  geo.setAttribute('position', quad.getAttribute('position'));
  geo.setAttribute('uv', quad.getAttribute('uv'));
  const banks = new Float32Array(n * 4);
  for (let i = 0; i < n; i++) banks.set([(rnd() - 0.5) * 36, (rnd() - 0.5) * 36, 2.5 + rnd() * 3.5, rnd()], i * 4);
  geo.setAttribute('aBank', new THREE.InstancedBufferAttribute(banks, 4));
  geo.instanceCount = n;
  const mat = new THREE.ShaderMaterial({
    vertexShader: BANK_VERT,
    fragmentShader: BANK_FRAG,
    uniforms: { uTime: { value: 0 }, uColor: { value: new THREE.Color(ctx.crypt ? '#2a2e36' : '#9aa2ae') }, uAlpha: { value: ctx.crypt ? 0.5 : 0.42 } },
    transparent: true,
    depthWrite: false,
  });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.frustumCulled = false;
  mesh.renderOrder = 6;
  const glow = new GlowLayer(512);
  group.add(mesh, glow.mesh);
  let heroes: number[] = [];
  let enemies: { id: number; kind: string }[] = [];
  const right = new THREE.Vector3();

  return {
    group,
    tick: (s: BattleState) => {
      heroes = s.heroes.filter((h) => h.downTicks === 0).map((h) => h.id);
      enemies = s.enemies.filter((e) => e.hp > 0 && e.kind !== 'boneWall').map((e) => ({ id: e.id, kind: e.kind }));
    },
    frame: (t, camera) => {
      (mat.uniforms.uTime as { value: number }).value = t;
      sky.pulse({});
      cameraRight(camera, right);
      glow.begin();
      for (const id of heroes) {
        const p = ctx.pos(id);
        if (!p) continue;
        const f = 0.92 + 0.08 * Math.sin(t * 7 + id);
        glow.add(p.x, p.y + 0.1, p.z, LAMP, 2.4, 0.16 * f);
        glow.add(p.x + right.x * 0.18, p.y + 0.55, p.z + right.z * 0.18, LAMP, 0.35, 0.7 * f);
      }
      // Eyes in the fog: two glints each, blinking now and then (scene fog does not dim glows).
      for (const e of enemies) {
        const p = ctx.pos(e.id);
        if (!p) continue;
        const y = p.y + eyeHeight(e.kind);
        const blink = Math.sin(t * 0.9 + e.id * 2.1) > 0.96 ? 0.05 : 1;
        for (const sgn of [-1, 1]) glow.add(p.x + right.x * 0.05 * sgn, y, p.z + right.z * 0.05 * sgn, EYE, 0.1, 0.95 * blink);
        glow.add(p.x, y, p.z, EYE_HOT, 0.3, 0.3 * blink);
      }
      glow.end();
    },
    dispose: () => {
      sky.restore();
      geo.dispose();
      quad.dispose();
      mat.dispose();
      glow.mesh.geometry.dispose();
      glow.material.dispose();
    },
  };
};
